import React, { useState } from 'react';
import * as yup from 'yup';
import { Box, Grid, Typography } from '@mui/material';
import { SelectField, TextField } from '@qrios/frontend-library';
import { Formik } from 'formik';
import { keys, length } from 'ramda';
import Divider from '../../../../../../../common/divider/divider';
import WithFormLayout from '../../../../../../../common/with-form-layout';
import { UserInvitationData } from '../../interfaces';
import RoleDescription from '../../../active/user-details/roleDescription';
import locales from '../../locales';
import { diffObject } from '../../../../../../../../constants/helpers/diff';
import { INVITATION_SETTINGS } from './interfaces';
import { UserRoleItem } from '../../../interfaces';

type BusinessError = any;

interface FormikInitialValues {
  [INVITATION_SETTINGS.ROLE]: string;
  [INVITATION_SETTINGS.EMAIL]: string;
}

interface Props {
  roles: UserRoleItem[];
  handleCloseDialog: () => void;
  handleSubmitAction: (userData: UserInvitationData) => void;
  loading: boolean;
  error?: BusinessError | null;
}

const validationSchema = yup.object().shape({
  email: yup.string().email('Invalid email address').required('Email address is required'),
  role: yup.string().required('Role is required')
});

const UserInvitationDetails = ({ roles, handleCloseDialog, handleSubmitAction, loading, error }: Props) => {
  const [mappedRoles] = useState<string[]>(roles.map(item => item.name));

  const formikInitialValues: FormikInitialValues = {
    [INVITATION_SETTINGS.ROLE]: '',
    [INVITATION_SETTINGS.EMAIL]: ''
  };

  return (
    <Formik
      initialValues={formikInitialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        const userInvitationData = {
          emailAddress: values.email,
          roleId: roles.find(item => item.name === values.role)?.id
        } as UserInvitationData;
        handleSubmitAction(userInvitationData);
      }}
    >
      {({ setFieldValue, values, errors, touched, handleBlur, handleSubmit }) => {
        const handleChange = (field: string, value: unknown) => {
          setFieldValue(field, value);
        };
        const valuesUnchanged =
          length(
            keys(
              diffObject(
                { email: INVITATION_SETTINGS.EMAIL, role: INVITATION_SETTINGS.ROLE },
                { email: values.email, role: values.role }
              )
            )
          ) === 0;

        return (
          <WithFormLayout
            formInfoText=""
            submitLabel="Invite user"
            secondaryLabel={valuesUnchanged ? 'Close' : 'Close without update'}
            onSubmit={handleSubmit}
            onSecondary={handleCloseDialog}
            loading={loading}
            error={error}
            disableSubmit={valuesUnchanged}
          >
            <Box mb={1}>
              <TextField
                data-testid="email-input"
                name={INVITATION_SETTINGS.EMAIL}
                value={values[INVITATION_SETTINGS.EMAIL]}
                handleChange={e => handleChange(e.target.name, e.target.value)}
                error={errors[INVITATION_SETTINGS.EMAIL] as string}
                touched={touched[INVITATION_SETTINGS.EMAIL] as boolean}
                label={locales.inviteModal.label.email}
                placeholder={locales.inviteModal.label.email}
                handleBlur={handleBlur}
              />
            </Box>
            <Divider />
            <Grid container>
              <Grid item xs={12}>
                <SelectField
                  name={INVITATION_SETTINGS.ROLE}
                  label={locales.updateUser.label.roleDescription}
                  value={values[INVITATION_SETTINGS.ROLE]}
                  handleChange={e => handleChange(e.target.name, e.target.value)}
                  error={errors[INVITATION_SETTINGS.ROLE] as string}
                  touched={touched[INVITATION_SETTINGS.ROLE] as boolean}
                  items={mappedRoles.map(item => ({ label: item, value: item }))}
                  handleBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={12} sx={{ p: 1 }}>
                {values.role && <Typography variant="h3">Role description</Typography>}
                <RoleDescription selectedRole={values.role} possibleRoles={roles} />
              </Grid>
            </Grid>
          </WithFormLayout>
        );
      }}
    </Formik>
  );
};

export default UserInvitationDetails;
